import { useEffect, useState } from "react";

import { api_without_cancellation } from "@/api/api";
import { calculateAge, formatCalendarDate } from "@/Utils/helper";
import bdayIcon from "@/assets/images/birthdayicon.svg";
import incidentIcon from "@/assets/images/incident.svg";
import avatarImage from "@/assets/images/avatar.svg";
import esignImg from "@/assets/esign_icon.svg";
import deleteImg from "@/assets/delete.svg";
import printImg from "@/assets/print.svg";
import downloadImg from "@/assets/download.svg";
import inboxImg from "@/assets/inbox.svg";
import "./DetailsPanel.css";
import NewChatIcon from "@/assets/NewChatIcon";
import ChatDocumentModal from "@/Components/DocumentModal/DocumentSideBar/ChatDocumentModal";
import fileNumberIcon from "@/assets/filenumber.svg";
const getDocName = (doc) =>
  doc?.document_name || doc?.name || doc?.file_name || "Untitled Document";

// "Lakeasha Johnson" -> "Johnson, Lakeasha". Naive: last token = surname.
const formatLastFirst = (fullName) => {
  const name = (fullName || "").trim();
  if (!name) return "Client Info";
  const parts = name.split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts.pop();
  return `${parts.join(" ")} ${last}`;
};

const tagName = (t) => (typeof t === "string" ? t : (t?.name ?? ""));

const DetailsPanel = ({
  document: doc,
  onClose,
  onSaved,
  onDeleted,
  fileUrl,
  firms,
  selectedFirm,
  users,
  chatLoading,
  chatError,
  handleSelectFirm,
  sendMessage,
  searchUsers,
  handleSelectUser,
  setUpdatedUser,
  setChatDocumentThread,
}) => {
  const docId = doc?.id;

  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [isCopilot, setIsCopilot] = useState(Boolean(doc?.is_available_for_copilot));
  const [togglingCopilot, setTogglingCopilot] = useState(false);

  // Reseed when the open document changes.
  useEffect(() => {
    setNameDraft("");
    setIsCopilot(Boolean(doc?.is_available_for_copilot));
  }, [docId]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchDoc = async (body) => {
    const res = await api_without_cancellation.patch(
      `/api/document-library/documents/${docId}/`,
      body
    );
    return res.data;
  };

  const saveName = async () => {
    const next = nameDraft.trim();
    if (!next || next === getDocName(doc)) {
      setNameDraft("");
      return;
    }
    setSavingName(true);
    try {
      await patchDoc({ document_name: next });
      onSaved?.({ document_name: next });
      setNameDraft("");
    } catch (e) {
      console.error(e?.response?.data?.detail || "Couldn’t rename the document");
    } finally {
      setSavingName(false);
    }
  };

  const toggleCopilot = async (checked) => {
    setIsCopilot(checked); // optimistic
    setTogglingCopilot(true);
    try {
      await patchDoc({ is_available_for_copilot: checked });
      onSaved?.({ is_available_for_copilot: checked });
    } catch (e) {
      setIsCopilot(!checked); // revert
      console.error(e?.response?.data?.detail || "Couldn’t update Copilot availability");
    } finally {
      setTogglingCopilot(false);
    }
  };

  const removeFromLibrary = async () => {
    try {
      await api_without_cancellation.delete(`/api/document-library/documents/${docId}/`);
      onDeleted?.();
      onClose?.();
    } catch (e) {
      console.error(e?.response?.data?.detail || "Couldn’t remove from library");
    }
  };

  const handlePrint = () => {
    const url = doc?.upload_url;
    if (!url) return;
    const iframe = window.document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    window.document.body.appendChild(iframe);
    iframe.onload = () => iframe.contentWindow?.print();
  };

  const handleDownload = () => {
    const url = doc?.upload_url;
    if (!url) return;
    const link = window.document.createElement("a");
    link.href = url;
    link.setAttribute("download", getDocName(doc));
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const age = calculateAge(doc?.client?.birthday || "");
  const birthday = formatCalendarDate(doc?.client?.birthday) || "";
  const incident = formatCalendarDate(doc?.source_incident_date);
  const tags = doc?.tags || [];
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [showModal, setShowModal] = useState(false);
  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);
  return (
    <div className="lib-details d-flex flex-column justify-content-between p-b-5">
      {/* ORIGINATING CASE */}
      <div className="d-flex flex-column">
        <span className="lib-details-heading">ORIGINATING CASE</span>

        <div
          className="lib-details-row side-bar-padding d-flex align-items-center"
          style={{ height: "21px", background: "var(--primary-2)" }}
        >
          <div className="lib-details-avatar m-r-5 d-flex align-items-center justify-content-center">
            <img src={doc?.client?.profile_pic_19p || avatarImage} alt="" />
          </div>
          <span className="lib-details-client" title={doc?.source_client_name}>
            {formatLastFirst(doc?.source_client_name)}
          </span>
        </div>

        <div
          className="lib-details-row p-l-5 p-r-5 d-flex align-items-center justify-content-between"
          style={{ height: "21px", background: "var(--primary-4)" }}
        >
          <div className="d-flex align-items-center" style={{ height: "21px" }}>
            <span className="m-r-5 d-flex align-items-center">
              <img src={bdayIcon} style={{ width: 15, height: 15 }} alt="" />
            </span>
            <p className="lib-details-value">{birthday}</p>
          </div>
          <div className="d-flex align-items-center" style={{ height: "21px" }}>
            <span className="m-r-5 lib-details-muted">age</span>
            <p className="lib-details-value">{age ?? ""}</p>
          </div>
        </div>

        <div
          className="lib-details-row p-l-5 p-r-5 d-flex align-items-center"
          style={{ height: "21px", background: "var(--primary-2)" }}
        >
          {doc?.case_type?.casetype_icon && (
            <span className="m-r-5 d-flex align-items-center">
              <img src={doc.case_type.casetype_icon} style={{ width: 15, height: 15 }} alt="" />
            </span>
          )}
          <p className="lib-details-value">{doc?.case_type?.name || "N/A"}</p>
        </div>

        <div
          className="lib-details-row p-l-5 p-r-5 d-flex align-items-center"
          style={{ height: "21px", background: "var(--primary-4)" }}
        >
          <span className="m-r-5 d-flex align-items-center">
            <img src={incidentIcon} style={{ width: 15, height: 15 }} alt="" />
          </span>
          <p className="lib-details-value">{incident || "N/A"}</p>
        </div>

        <div
          className="lib-details-row p-l-5 p-r-5 d-flex align-items-center"
          style={{ height: "21px", background: "var(--primary-2)" }}
        >
          <span className="m-r-5 d-flex align-items-center">
            <i
              className="ic ic-litigation d-flex align-items-center justify-content-center"
              style={{ width: 15, height: 15 }}
              alt=""
            />
          </span>
          <p className="lib-details-value">
            {" "}
            {doc?.source_case_number ? `# ${doc.source_case_number}` : "Add case #"}
          </p>
        </div>
        <div
          className="lib-details-row p-l-5 p-r-5 d-flex align-items-center"
          style={{ height: "21px", background: "var(--primary-4)" }}
        >
          <span className="m-r-5 d-flex align-items-center">
            <img src={fileNumberIcon} style={{ width: 15, height: 15 }} alt="" />
          </span>
          <p className="lib-details-value">{doc?.source_file_number || ""}</p>
        </div>
      </div>
      {/* DOCUMENT NAME */}
      <div>
        <span className="lib-details-heading m-t-5">DOCUMENT NAME</span>
        <div className="p-l-5 p-r-5" style={{ background: "" }}>
          <p
            style={{ background: "var(--primary-2)" }}
            className="lib-details-docname"
            title={getDocName(doc)}
          >
            {getDocName(doc)}
          </p>
          <div className="d-flex align-items-center" style={{ gap: 5 }}>
            <input
              type="text"
              className="form-control p-l-5 d-flex-1 document-search-text-color"
              placeholder="Rename Doc"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveName()}
              style={{ height: 25, fontWeight: 600, flex: 1 }}
            />
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center justify-content-center height-25"
              style={{ fontWeight: 600, fontSize: 14 }}
              onClick={saveName}
              disabled={savingName || !nameDraft.trim()}
            >
              {savingName ? "…" : "Save"}
            </button>
          </div>
        </div>
      </div>
      {/* TAGS */}
      <div>
        <span className="lib-details-heading m-t-5">TAGS</span>
        <div className="lib-details-tags side-bar-padding d-flex flex-wrap align-items-center justify-content-center">
          {tags.length === 0 ? (
            <span className="lib-details-muted">No tags</span>
          ) : (
            tags.map((t) => (
              <span key={t?.id ?? tagName(t)} className="lib-details-tag">
                #{tagName(t)}
              </span>
            ))
          )}
        </div>
      </div>

      <div className="d-flex flex-column" style={{ gap: height <= 1024 ? "8px" : "40px" }}>
        <div>
          <div
            className=""
            style={{
              display: height <= 1024 ? "flex" : "flex",
              justifyContent: height <= 1024 ? "space-between" : "initial",
              background: "white",
              padding: "5px",
              gap: "5px",
              flexDirection: height <= 1024 ? "row" : "column",
              flexWrap: height <= 1024 ? "nowrap" : "nowrap",
            }}
          >
            {/* Chat Document Link Dropdown */}

            <div className="tile-row w-100  center-content my-auto">
              <div className="dropdown  d-flex">
                <button
                  className={
                    "btn height-25 bottom-sidebar-buttons-things  dropdown-toggle position-relative d-flex align-items-center justify-content-center w-100 form-select has-no-bg position-relative doc-rev-btn rotate-btn "
                  }
                  type="button"
                  onClick={handleShow}
                >
                  <span className="ic-15 d-flex align-items-center justify-content-center">
                    {/* <i className="ic ic-19 ic-chat-3d d-flex align-items-cente justify-content-center" /> */}
                    <NewChatIcon />
                  </span>
                  {height <= 1024 ? "Chat" : "Chat About Document"}
                </button>
              </div>
            </div>
            <div className="tile-row w-100  center-content my-auto">
              <div className="dropdown  d-flex">
                <button
                  className={
                    "btn height-25 bottom-sidebar-buttons-things  dropdown-toggle position-relative d-flex align-items-center justify-content-center w-100 form-select has-no-bg position-relative doc-rev-btn rotate-btn "
                  }
                  type="button"
                  onClick={removeFromLibrary}
                >
                  <span
                    style={{
                      width: "15px",
                      height: "15px",
                    }}
                    className="ic-15 "
                  >
                    <i className="ic-15 ic ic-library d-flex align-items-center justify-content-center" />
                  </span>
                  {height <= 1024 ? "Remove" : "Remove from Library"}
                </button>
              </div>
            </div>
          </div>
          <label
            className="lib-details-copilot side-bar-padding d-flex align-items-center"
            style={{ gap: 5 }}
          >
            <input
              type="checkbox"
              checked={isCopilot}
              disabled={togglingCopilot}
              onChange={(e) => toggleCopilot(e.target.checked)}
            />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Available in CoPilot Library</span>
          </label>
        </div>
        <div
          className="lib-details-grid"
          style={{
            padding: "0 5px",
          }}
        >
          <button className="btn btn-primary col height-25 lib-grid-btn" onClick={handlePrint}>
            <img src={printImg} alt="" /> Print
          </button>
          <button className="btn btn-primary col height-25 lib-grid-btn" onClick={handleDownload}>
            <img src={downloadImg} alt="" /> Download
          </button>
          <button
            className="btn btn-primary col height-25 lib-grid-btn"
            disabled
            title="Not available in library"
          >
            <i className="ic ic-15 ic-email-3d" /> Email
          </button>
          <button
            className="btn btn-secondary col height-25 lib-grid-btn"
            disabled
            title="Not available in library"
          >
            <img src={inboxImg} alt="" /> Inbox
          </button>
          <button
            className="btn btn-primary col height-25 lib-grid-btn"
            disabled
            title="Not available in library"
          >
            <img src={esignImg} alt="" /> E-Sign
          </button>
          <button
            className="btn btn-secondary col height-25 lib-grid-btn"
            onClick={removeFromLibrary}
          >
            <img src={deleteImg} alt="" /> Delete
          </button>
        </div>
      </div>

      {showModal && (
        <ChatDocumentModal
          showModal={showModal}
          handleClose={handleClose}
          documentURL={fileUrl}
          docName={getDocName(doc)}
          documentData={doc}
          userId={localStorage.getItem("loggedInUser") || null}
          clientId={doc?.client?.id}
          caseId={doc?.source_case_id}
          firms={firms}
          selectedFirm={selectedFirm}
          users={users}
          loading={chatLoading}
          error={chatError}
          handleSelectFirm={handleSelectFirm}
          sendMessage={sendMessage}
          searchUsers={searchUsers}
          setChatDocumentThread={setChatDocumentThread}
          setUpdatedUser={setUpdatedUser}
          handleSelectUser={handleSelectUser}
        />
      )}
    </div>
  );
};

export default DetailsPanel;
